import { HttpClient } from '@angular/common/http';
import { Injectable, computed, effect, inject, resource, signal, untracked } from '@angular/core';
import { TAKE_ALL, ascending, valueOf, readPreference, removePreference, writePreference } from 'eshava-shared';
import { firstValueFrom } from 'rxjs';

import { appSettings } from '@portal/settings';

export interface Product {
  readonly id: number;
  readonly name: string;
  readonly subscriptionId: number;
  readonly subscriptionName: string;
  readonly tenantId: number;
  readonly tenantName: string;
}

export interface SubscriptionGroup {
  readonly subscriptionId: number;
  /** What the group is called in the dropdown -- see `groups` for when the tenant is part of it. */
  readonly label: string;
  readonly products: readonly Product[];
}

/**
 * Where the last selection is kept.
 *
 * Prefixed like every other preference this application writes, so what belongs to the portal is
 * recognisable in a browser that holds more than one application's storage.
 */
const PRODUCT_PREFERENCE = 'portal.product';

/** What the search answers. Declared here rather than imported: `core` must not reach into a feature. */
interface ProductSearchResponse {
  applicationTypes: {
    id: number;
    name: string;
    subscriptionId: number;
    subscription: string;
    tenantId: number;
    tenant: string;
  }[];
}

/**
 * The selected product, which everything outside `settings` is shown for.
 *
 * The chain is subscription → product; the application is not part of it. **One grouped dropdown**
 * rather than two chained ones: a product belongs to exactly one subscription, so choosing the
 * subscription first would cost a click for information the product already carries.
 *
 * **It loads its own list.** The obvious alternative was to reuse `ProductsClient` from the settings
 * feature, and it is the wrong direction: `core` must not depend on a feature, and the context is
 * needed after every sign-in whether or not settings is ever opened. What is duplicated is one POST
 * with an empty filter; what is avoided is the shell being unable to start without a feature.
 *
 * **The selection is in the address and in `localStorage`, and the two answer different questions.**
 * The address makes a link mean the same thing for whoever opens it -- see `ProductRequired`, which
 * owns that reconciliation. The stored value is what makes a **reload** keep the context, and only
 * that: it is a convenience for this browser, never something a link carries.
 *
 * **The address wins where both have something to say.** A link that names a product has to show that
 * product, whatever this browser looked at last -- otherwise the link is not a link.
 */
@Injectable({ providedIn: 'root' })
export class ProductContextService {
  private readonly http = inject(HttpClient);

  private readonly _selectedId = signal<number | null>(null);

  /** Restored at most once. Doing it again would undo a deliberate clearing on the next reload. */
  private restored = false;

  private readonly loaded = resource({
    loader: () => this.load()
  });

  readonly products = computed<readonly Product[]>(() => ((valueOf(this.loaded) ?? [])));
  readonly selectedId = this._selectedId.asReadonly();
  readonly isLoading = this.loaded.isLoading;

  readonly selected = computed(() => this.products().find(product => product.id === this._selectedId()) ?? null);

  readonly hasProducts = computed(() => this.products().length > 0);

  readonly groups = computed(() => groupProducts(this.products()));

  constructor() {
    /**
     * What this browser looked at last, if it is still there.
     *
     * **Only while nothing is selected**, and **only once**. `ProductRequired` applies the address
     * first where it names a product, so this fills the gap rather than competing with it -- and a
     * restore that ran twice would put the selection back the moment someone cleared it.
     *
     * A stored product the user can no longer see is dropped rather than reported. That is the same
     * rule as everywhere else here: from their side those records do not exist, so there is nothing
     * to say about one.
     */
    effect(() => {
      const products = this.products();

      if (products.length === 0) {
        return;
      }

      untracked(() => {
        if (this.restored) {
          return;
        }

        this.restored = true;

        if (this._selectedId() !== null) {
          return;
        }

        const stored = Number(readPreference(PRODUCT_PREFERENCE));

        if (Number.isInteger(stored) && products.some(product => product.id === stored)) {
          this._selectedId.set(stored);
        }
      });
    });

    /**
     * One product is not a choice, so it is made.
     *
     * The same rule as the tenant field when creating a subscription, and for most customers it
     * removes the empty state entirely -- they hold one product and never see a screen asking them
     * to pick it. Only while nothing is selected: a preselection that reasserted itself would make
     * the dropdown impossible to clear.
     *
     * `untracked` around the write, or reading the selection to decide would make this an effect
     * that depends on the signal it sets -- which does not error, it hangs the tab.
     */
    effect(() => {
      const products = this.products();

      if (products.length !== 1) {
        return;
      }

      untracked(() => {
        if (this._selectedId() === null) {
          this._selectedId.set(products[0].id);
        }
      });
    });
  }

  /**
   * Fetch again -- after a product is created, renamed or removed in the settings area.
   *
   * Called from there rather than watched from here: a feature may depend on `core`, and the reverse
   * would be the dependency this service exists to avoid.
   */
  reload(): void {
    this.loaded.reload();
  }

  /**
   * Choosing a product, and remembering it for this browser.
   *
   * **Clearing clears the stored value too.** Leaving it behind would bring the product back on the
   * next reload, which is the same trap as a preselection that reasserts itself: the dropdown would
   * be impossible to empty for longer than one session.
   */
  select(id: number | null): void {
    this._selectedId.set(id);

    /* Nothing is restored after this point anyway -- but saying so here keeps the flag honest for a
       selection made before the list has finished loading. */
    this.restored = true;

    if (id === null) {
      removePreference(PRODUCT_PREFERENCE);
    } else {
      writePreference(PRODUCT_PREFERENCE, String(id));
    }
  }

  /**
   * Every product the signed-in user may see, in one call.
   *
   * Sorted by subscription and then by product name, which is the order the dropdown reads in -- the
   * grouping preserves it, so nothing is sorted again here. Both fields are sortable on the endpoint.
   */
  private async load(): Promise<readonly Product[]> {
    const filter = {
      skip: 0,
      take: TAKE_ALL,
      filterFields: {},
      sortFields: { subscription: ascending(0), name: ascending(1) }
    };

    const response = await firstValueFrom(
      this.http.post<ProductSearchResponse>(appSettings.endpoints.accounts.products + 'search', { filter })
    );

    return (response.applicationTypes ?? []).map(product => ({
      id: product.id,
      name: product.name,
      subscriptionId: product.subscriptionId,
      subscriptionName: product.subscription,
      tenantId: product.tenantId,
      tenantName: product.tenant
    }));
  }
}

/**
 * Products grouped under their subscription, in the order they arrived.
 *
 * **The tenant joins the group label only where more than one is in view.** Two subscriptions can
 * carry the same name in different tenants, and then "Production" above "Production" is a menu nobody
 * can use. Where everything belongs to one tenant the name stands alone, because a suffix that is the
 * same on every line carries no information and costs the width.
 *
 * **Asked of the list rather than of the permissions**, and the difference is deliberate: someone
 * allowed to act in three tenants but holding products in one has nothing to disambiguate. What
 * decides is what is on screen.
 *
 * A function rather than a method so the rule can be tested without a service, an injector or a
 * server -- it is the only part of this file with a decision in it.
 */
export function groupProducts(products: readonly Product[]): readonly SubscriptionGroup[] {
  const grouped = new Map<number, Product[]>();

  for (const product of products) {
    const existing = grouped.get(product.subscriptionId);

    if (existing === undefined) {
      grouped.set(product.subscriptionId, [product]);
    } else {
      existing.push(product);
    }
  }

  const withTenant = new Set(products.map(product => product.tenantId)).size > 1;

  return [...grouped.values()].map(entries => ({
    subscriptionId: entries[0].subscriptionId,
    label: withTenant ? `${entries[0].subscriptionName} (${entries[0].tenantName})` : entries[0].subscriptionName,
    products: entries
  }));
}
